import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { I18nModule } from '@spartacus/core';
import { FileUploadModule } from 'primeng/fileupload';
import {
  DragAndDropConfigs,
  DragAndDropFile,
  DragAndDropReport,
  DragAndDropValidation,
  FILE_ERROR_TYPE,
} from './core/drag-and-drop.model';
import { DragAndDropService } from './drag-and-drop.service';
import { ScreenViewModule } from '../../core/directives/screen-view/_screen-view.module';
import { DEVICE_SIZE } from '../../core/models/others.model';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { ToasterService } from '../toaster/toaster.service';
import { TOASTER_TYPE } from '../toaster/toaster.model';

@Component({
  selector: 'drag-and-drop',
  standalone: true,
  imports: [CommonModule, I18nModule, FileUploadModule, ScreenViewModule, TooltipModule],
  templateUrl: './drag-and-drop.component.html',
  styleUrl: './drag-and-drop.component.scss',
})
export class DragAndDropComponent implements OnInit, OnDestroy {
  @Input() validation: DragAndDropValidation = this.dragAndDropService.validateFiles.bind(this.dragAndDropService);
  @Input() clearFiles$: Observable<void> = new Observable();
  @Input() configs: DragAndDropConfigs = {
    maxFiles: 5,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedFileTypes: ['jpg', 'jpeg', 'gif', 'tif', 'tiff', 'png', 'doc', 'docx', 'pdf', 'ppt', 'pptx'],
  };

  @Output() _onValidateFiles = new EventEmitter<DragAndDropReport>();

  DEVICE_SIZE = DEVICE_SIZE;
  subscription = new Subscription();
  isDragging = false;
  dragAndDropReport: DragAndDropReport = { items: [] };
  files: File[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    protected dragAndDropService: DragAndDropService,
    protected toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    this.checkClearFiles();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  checkClearFiles() {
    this.subscription.add(
      this.clearFiles$.subscribe(() => {
        this.clear();
      }),
    );
  }

  handlerDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
    this.cdr.detectChanges();
  }

  handlerLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = false;
    this.cdr.detectChanges();
  }

  handlerDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : [];
    this.setFiles(files);
  }

  handlerChange(input: HTMLInputElement) {
    const files = Array.from(input.files || []);
    this.setFiles(files);
  }

  setFiles(dirtFiles: File[]) {
    this.toasterService.closeALL();
    this.isDragging = false;

    const unRepeatedItems = dirtFiles.filter(
      file => !this.dragAndDropReport.items?.find(f => f.file?.name === file.name),
    );

    const items: DragAndDropFile[] = [
      ...this.files.map(file => ({ file, errors: [] as any[] })),
      ...unRepeatedItems.map(file => ({ file, errors: [] as any[] })),
    ];

    this.dragAndDropReport = this.validation(items, this.configs);
    const validItems = this.dragAndDropReport.items?.filter(file => !file.errors.length);
    const invalidItems = this.dragAndDropReport.items?.filter(file => file.errors.length);
    this.dragAndDropReport.items = validItems?.slice(0, this.configs.maxFiles) || [];
    this.files = this.dragAndDropReport.items.map(f => f.file) as File[];

    if (this.dragAndDropReport.genericError?.type === FILE_ERROR_TYPE.MAX_FILES) {
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: this.dragAndDropReport.genericError.message,
      });
    }

    invalidItems?.forEach(item => {
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: item.errors[0].message,
      });
    });

    this._onValidateFiles.emit({
      genericError: this.dragAndDropReport.genericError,
      items: this.files.map(file => ({ file, errors: [] as any[] })),
    });

    this.cdr.detectChanges();
  }

  removeFile(file: File) {
    const index = this.dragAndDropReport.items?.findIndex(f => f.file === file);

    if (index !== undefined && index > -1 && this.dragAndDropReport.items) {
      this.dragAndDropReport.items.splice(index, 1);

      this.files = this.dragAndDropReport.items.map(f => f.file) as File[];
      this._onValidateFiles.emit(this.dragAndDropReport);
      this.cdr.detectChanges();
    }
  }

  clear() {
    this.dragAndDropReport = { items: [] };
    this.files = [];

    this._onValidateFiles.emit({
      items: [],
    });

    this.cdr.detectChanges();
  }
}
